import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { FC, useCallback, useEffect, useState } from 'react'
import { useGalleryAssignment, useGalleryDeal, useGalleryVisualsMeta } from '../../contexts'

import { AutoAwesomeOutlined } from '@mui/icons-material'
import { Box } from '@mui/material'
import { CopyGalleryLink } from '../../../common'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { MUIButton } from 'components/common/MUIButton'
import { MUITooltip } from 'components/common/MUITooltip'
import { OrderCancellationPopup } from 'components/common/OrderCancellationPopup'
import { PageActions } from 'components/common/Page/PageActions'
import { Path } from 'constants/router'
import { RescheduleOrderModal } from 'components/common/RescheduleOrderModal'
import { externalNavigate } from 'utils/helpers'
import { getPricingUrl } from 'utils/localization'
import i18n from 'translations/i18n'
import { useDynamicNow, useWorkspacePlan } from 'utils/hooks'
import { useNavigate } from 'react-router'
import { useRescheduleOrderModule } from 'components/common/RescheduleOrder'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Client gallery page actions controller.
 * 
 * @example <ClientGalleryPageActionsController />
 */
export const ClientGalleryPageActionsController: FC = () => {
  const { t } = useTranslation(['gallery'])
  const navigate = useNavigate()
  const dynamicNow = useDynamicNow()
  const { purchasedVisualsExist } = useGalleryVisualsMeta()
  const { dealData, allowCancelOrder } = useGalleryDeal()

  const { isReschedulePossible } = useRescheduleOrderModule()

  const {
    product,
    assignmentData,
    logGalleryEvent,
  } = useGalleryAssignment()

  const {
    clientData,
    currentUserWorkspace
  } = useUserData()
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  const [disableButton, setDisableButton] = useState(true)
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false)
  const [isCancellationPopupOpen, setIsCancellationPopupOpen] = useState<boolean>(false)
  const handleGoToAIGenerator = useCallback(() => {
    if (!assignmentData?.id) return

    // ANALYTICS
    logAnalyticsEvent('Open Generate Text', {
      userEmail: clientData?.email,
      planId: currentUserWorkspace?.id,
      planName: currentUserWorkspace?.name,
      assignmentId: assignmentData.id,
      productCategory: product?.category,
      productKind: product?.kind,
    })

    navigate(
      Path.GALLERY_VISUALS_MARKETING.replace(':id', assignmentData.id.toString())
    )
  }, [assignmentData?.id, clientData?.email, currentUserWorkspace?.id, currentUserWorkspace?.name, navigate, product?.category, product?.kind])

  const handleCheckSubscriptionClick = () => {
    if (!hasWorkspaceActiveSubscription) {
      logGalleryEvent(AnalyticsEvent.GALLERY_SAAS_UPSELL_TEXT_GENERATOR)
      externalNavigate(getPricingUrl(i18n.language))
    }
  }

  useEffect(() => {
    setDisableButton(!purchasedVisualsExist || !hasWorkspaceActiveSubscription)
  }, [purchasedVisualsExist, hasWorkspaceActiveSubscription])

  return (
    <PageActions>

      <CopyGalleryLink />

      {allowCancelOrder ?
        <MUIButton
          type="orangePrimary"
          onClick={() => setIsCancellationPopupOpen(true)}
        >
          {t('client_gallery.cancel_order')}
        </MUIButton>
        :
        <MUITooltip
          content={!hasWorkspaceActiveSubscription && t('only_for_subscription')}
        >
          <Box onClick={handleCheckSubscriptionClick}>
            <MUIButton
              type="defaultPrimary"
              disabled={disableButton}
              onClick={handleGoToAIGenerator}
              startIcon={<AutoAwesomeOutlined fontSize='large' />}
              endIcon={!hasWorkspaceActiveSubscription && <LockOutlinedIcon fontSize='large' />}
            >
              {t('client_gallery.ai_text_generator_tool')}
            </MUIButton>
          </Box>
        </MUITooltip>
      }

      {isReschedulePossible &&
        <MUIButton onClick={() => setIsRescheduleOpen(true)}>
          {t('reschedule')}
        </MUIButton>
      }

      {!!dealData &&
        <OrderCancellationPopup
          data={dealData}
          isOpen={isCancellationPopupOpen}
          dynamicNow={dynamicNow}
          onClickClose={() => setIsCancellationPopupOpen(false)}
        />
      }

      <RescheduleOrderModal
        isOpen={isRescheduleOpen}
        onClose={() => setIsRescheduleOpen(false)}
      />

    </PageActions>
  )
}
